// =============================================================================
// JobSearchBar: the Discover/Jobs tab's search box (brief (b): "the POST
// /api/jobs/search box — one input, results through the same card renderer")
// + the "Check for new jobs" button (brief (b): "hitting POST
// /api/watchlist/poll with honest progress — the served summary, no invented
// counts"). A plain <input type="search"> (not a VoiceTextarea) — project
// rule #2 is scoped to textareas; a one-line search field is not one, and the
// mockup's own search affordances elsewhere in this app follow the same
// distinction (VoiceTextarea is reserved for multi-line capture, e.g.
// ImportPasteCard's resume paste).
//
// QA #19 ("'Poll'/'Poll now' is engineer jargon" — Jason: "makes no sense to
// most people, only us nerds"): the VISIBLE button label reads "Check for new
// jobs" (verbiage TBD — see this lane's build report for the confirm ask).
// Everything ELSE named "poll" here (onPollNow/PollState/the underlying POST
// /watchlist/poll route) is internal plumbing, never rendered — this file's
// own prop/type names are deliberately unchanged; only user-facing copy moved.
//
// Poll progress is rendered EXACTLY from PollSummary's real fields (api.ts) —
// no percentage bar (the server has no progress stream for this route, same
// honesty law ImportPasteCard's header documents for POST /corpus/import),
// just an indeterminate spinner while in flight and the served counts once done.
// =============================================================================

import { useEffect, useState } from 'react';
import type { PollState } from '../lib/useJobsInbox';
import styles from './JobSearchBar.module.css';

export interface JobSearchBarProps {
  query: string;
  loading: boolean;
  onSearch: (query: string) => void;
  onClear: () => void;
  poll: PollState;
  onPollNow: () => void;
}

export function JobSearchBar({ query, loading, onSearch, onClear, poll, onPollNow }: JobSearchBarProps) {
  const [draft, setDraft] = useState(query);

  // Keep the input's visible text in sync with the hook's own query state —
  // defensive against any FUTURE caller of clearSearch/runSearch that isn't
  // this component's own handleClear (which already syncs draft locally).
  // A controlled input desyncing from its source of truth is a class of bug
  // this codebase's other forms avoid by not needing local draft state at
  // all; JobSearchBar needs its own (so typing doesn't fire a search on every
  // keystroke), so this effect is the guard against it drifting.
  useEffect(() => {
    setDraft(query);
  }, [query]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSearch(draft);
  }

  function handleClear() {
    setDraft('');
    onClear();
  }

  const polling = poll.phase === 'polling';

  return (
    <div className={styles.wrap}>
      <form className={styles.searchRow} onSubmit={handleSubmit}>
        <div className={styles.inputWrap}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
            className={styles.searchIcon}
          >
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            type="search"
            className={styles.input}
            placeholder="Search every job we've stored…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={loading}
            aria-label="Search jobs"
          />
        </div>
        <button type="submit" className={styles.searchButton} disabled={loading || !draft.trim()}>
          {loading ? <span className={styles.spinner} aria-hidden="true" /> : 'Search'}
        </button>
        {query ? (
          <button type="button" className={styles.clearButton} onClick={handleClear} disabled={loading}>
            Clear
          </button>
        ) : null}
      </form>

      <div className={styles.pollRow}>
        <button type="button" className={styles.pollButton} onClick={onPollNow} disabled={polling}>
          {polling ? (
            <>
              <span className={styles.spinner} aria-hidden="true" />
              Checking your sources…
            </>
          ) : (
            'Check for new jobs'
          )}
        </button>
        {poll.phase === 'done' && poll.summary ? (
          <span className={styles.pollResult} role="status">
            {describePollSummary(poll.summary)}
          </span>
        ) : null}
        {poll.phase === 'error' && poll.error ? (
          <span className={styles.pollError} role="alert">
            {poll.error}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** Honest, count-only summary of what the server actually reported this poll
 *  pass — never a guessed or rounded figure, and never claims a count the
 *  response omitted. Mirrors ImportPasteCard.tsx's describeResult pattern. */
function describePollSummary(summary: PollState['summary']): string {
  if (!summary) return '';
  const parts: string[] = [];
  if (summary.ingestedNew > 0) {
    parts.push(`${summary.ingestedNew} new job${summary.ingestedNew === 1 ? '' : 's'}`);
  }
  if (summary.skipped > 0) {
    parts.push(`${summary.skipped} skipped`);
  }
  const base =
    parts.length > 0
      ? `Found ${parts.join(', ')}.`
      : summary.sourcesPolled > 0
        ? `Checked ${summary.sourcesPolled} source${summary.sourcesPolled === 1 ? '' : 's'} — nothing new.`
        : 'No sources to check yet.';
  const errNote = summary.errors.length > 0 ? ` (${summary.errors.length} source error)` : '';
  return `${base}${errNote}`;
}
